import { useState } from "react";
import { GstRestS } from "../../rest/gst-rest-service";
import {
  cleanGstin as cleanGstinUtil,
  cleanUsername as cleanUsernameUtil,
  isValidGstin,
  GST_MODAL_TITLES,
  GST_MODAL_SUBTITLES,
} from "./gstUtils";
import type { ModalStep } from "./gstUtils";
import { useOtpCountdown } from "./useOtpCountdown";
import { useOtpInput } from "./useOtpInput";

/**
 * Configuration injected by the consumer to customize the shared connection flow.
 */
export interface GstConnectionConfig {
  /** Initial GSTIN value (e.g. for reconnect flows). */
  initialGstin?: string;
  /** Initial username value (e.g. for reconnect flows). */
  initialUsername?: string;
  /** WhatsApp support message text (consumer-specific). */
  supportMessage: string;
  /**
   * Called when OTP verification succeeds. Receives the cleaned gstin, username,
   * and the fetchJobId from the backend response.
   */
  onConnected: (result: {
    gstin: string;
    username: string;
    fetchJobId: string | null;
  }) => void;
  /**
   * Optional callback fired when the backend returns an OTP verification failure.
   * Lets consumers map specific backend messages to user-facing error text.
   * If omitted, the raw `res.message` (or "Incorrect OTP") is used.
   */
  mapOtpError?: (message: string | undefined) => string;
  /**
   * Optional tracking callbacks. If provided, they are invoked at the appropriate
   * points in the flow (button clicks, errors, connection success/failure).
   */
  tracking?: {
    onButtonClicked?: (label: string, location: string) => void;
    onErrorShown?: (
      form: string,
      errorType: string,
      message: string,
      field?: string
    ) => void;
    onProgressUpdated?: (event: string, props: Record<string, unknown>) => void;
  };
}

/**
 * Shared GST connection state and logic — used by both supplier-intelligence
 * and lending/underwriting GST flows.
 *
 * Owns: step management, form state, OTP state, AUTH4037 escalation,
 * OTP_LIMIT_EXCEEDED handling, resend timer, and API calls.
 *
 * Consumers inject: initial values, support message, onConnected callback,
 * optional OTP error mapping, and optional tracking.
 */
export function useGstConnection(config: GstConnectionConfig) {
  const {
    initialGstin,
    initialUsername,
    supportMessage,
    onConnected,
    mapOtpError,
    tracking,
  } = config;

  // Nullish, not just missing — backend responses can send `null` (e.g. a
  // PAN-discovered credential with no username yet), and default params
  // only cover `undefined`.
  const safeInitialGstin = initialGstin ?? "";
  const safeInitialUsername = initialUsername ?? "";

  const track = tracking ?? {};
  const trackButton = track.onButtonClicked ?? (() => {});
  const trackError = track.onErrorShown ?? (() => {});
  const trackProgress = track.onProgressUpdated ?? (() => {});

  const [step, setStep] = useState<ModalStep>("form");

  // Form step
  const [gstin, setGstin] = useState(safeInitialGstin);
  const [gstUsername, setGstUsername] = useState(safeInitialUsername);
  const [apiEnabled, setApiEnabled] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [formServerError, setFormServerError] = useState(false);
  const [auth4037Count, setAuth4037Count] = useState(0);
  const [showAuth4037, setShowAuth4037] = useState(false);
  const [otpLimited, setOtpLimited] = useState(false);

  // OTP step — shared hooks
  const [otpLoading, setOtpLoading] = useState(false);
  const {
    otp,
    error: otpError,
    isFilled: isOtpFilled,
    inputRefs,
    setError: setOtpError,
    handleChange: handleOtpChange,
    handleKeyDown: handleOtpKeyDown,
    handlePaste: handleOtpPaste,
    reset: resetOtp,
  } = useOtpInput();
  const { countdown, start: startCountdown } = useOtpCountdown();

  const isFormValid =
    isValidGstin(gstin) && gstUsername.trim().length > 0 && apiEnabled;

  const cleanGstin = () => cleanGstinUtil(gstin);
  const cleanUsername = () => cleanUsernameUtil(gstUsername);

  const handleVerifyAccount = async () => {
    if (step === "form") {
      if (!isFormValid) return;
      setFormLoading(true);
      setFormError("");
      setFormServerError(false);
      try {
        const res = await GstRestS.generateOtp(cleanGstin(), cleanUsername());
        if (res.success) {
          setShowAuth4037(false);
          setOtpLimited(false);
          setStep("otp");
          startCountdown();
          setTimeout(() => inputRefs.current[0]?.focus(), 100);
        } else if (res.errorCode === "OTP_LIMIT_EXCEEDED") {
          setOtpLimited(true);
          setShowAuth4037(false);
          setFormError("");
          trackError(
            "gstin_form",
            "otp_limit_exceeded",
            res.message || "OTP_LIMIT_EXCEEDED",
            "gstin"
          );
        } else if (res.errorCode === "AUTH4037") {
          setAuth4037Count((c) => c + 1);
          setShowAuth4037(true);
          setFormError("");
          trackError(
            "gstin_form",
            "gstin_api_access_disabled",
            res.message || "AUTH4037",
            "gstin"
          );
        } else {
          const msg =
            res.message || "Failed to send OTP. Check your GSTIN and username.";
          setFormError(msg);
          setShowAuth4037(false);
          trackError("gstin_form", "gstin_format_invalid", msg, "gstin");
        }
      } catch (err: any) {
        const isServerError = (err?.response?.status ?? 0) >= 500;
        const msg = isServerError
          ? "Something went wrong. Please contact Merito."
          : "Something went wrong. Please try again.";
        setFormError(msg);
        setFormServerError(isServerError);
        setShowAuth4037(false);
        trackError("gstin_form", "gstin_format_invalid", msg);
      } finally {
        setFormLoading(false);
      }
    } else if (step === "otp") {
      if (!isOtpFilled) return;
      setOtpLoading(true);
      setOtpError("");
      try {
        const res = await GstRestS.verifyOtp(
          cleanGstin(),
          cleanUsername(),
          otp.join("")
        );
        if (res.success) {
          trackProgress("gstin_connected", {
            gstin: cleanGstin(),
            success: true,
            context: initialGstin ? "reconnect" : "new",
          });
          onConnected({
            gstin: cleanGstin(),
            username: cleanUsername(),
            fetchJobId: res.data?.fetchJobId
              ? String(res.data.fetchJobId)
              : null,
          });
        } else {
          const msg = mapOtpError
            ? mapOtpError(res.message)
            : res.message || "Incorrect OTP";
          setOtpError(msg);
          trackProgress("gstin_connected", {
            gstin: cleanGstin(),
            success: false,
            error_code: "otp_invalid",
            context: initialGstin ? "reconnect" : "new",
          });
          trackError("otp_form", "otp_invalid", msg, "otp");
        }
      } catch {
        const msg = "Verification failed. Please try again.";
        setOtpError(msg);
        trackError("otp_form", "otp_invalid", msg, "otp");
      } finally {
        setOtpLoading(false);
      }
    }
  };

  const handleResend = async (force = false) => {
    if (!force && countdown > 0) return;
    trackButton("Resend OTP", "connect_modal");
    try {
      await GstRestS.generateOtp(cleanGstin(), cleanUsername());
      resetOtp();
      startCountdown();
    } catch {
      setOtpError("Failed to resend OTP.");
    }
  };

  const isLoading = step === "form" ? formLoading : otpLoading;
  const canSubmit =
    step === "form"
      ? isFormValid && !formLoading
      : step === "otp"
      ? isOtpFilled && !otpLoading
      : false;

  // ── AUTH4037 escalation (form step) ─────────────────────────────────────────
  const showAuthBanner = showAuth4037 && step === "form";
  const contactMode =
    (showAuthBanner && auth4037Count >= 3) ||
    (step === "form" && formServerError);
  const authBanner =
    auth4037Count >= 3
      ? {
          title: "Still not connecting",
          body: "Contact us and we'll help you get connected.",
        }
      : auth4037Count === 2
      ? {
          title: "Still not connecting",
          body: "Check that you're using the right username for this GSTIN.",
        }
      : {
          title: "Couldn't connect",
          body: "Have you enabled API access for this GSTIN?",
        };

  const primaryText = isLoading
    ? "Verifying..."
    : contactMode
    ? "Contact Us"
    : showAuthBanner && auth4037Count === 2
    ? "Retry"
    : showAuthBanner && auth4037Count === 1
    ? "I've enabled it, retry"
    : "Verify Account";

  const modalTitle =
    step === "form"
      ? GST_MODAL_TITLES.form
      : step === "otp"
      ? GST_MODAL_TITLES.otp
      : GST_MODAL_TITLES.api_setup;
  const modalSubtitle =
    step === "form"
      ? GST_MODAL_SUBTITLES.form
      : step === "otp"
      ? GST_MODAL_SUBTITLES.otp
      : GST_MODAL_SUBTITLES.api_setup;

  return {
    // State
    step,
    setStep,
    gstin,
    gstUsername,
    apiEnabled,
    formError,
    otpError,
    otp,
    countdown,
    inputRefs,
    showAuthBanner,
    authBanner,
    auth4037Count,
    otpLimited,
    isLoading,
    canSubmit,
    contactMode,
    primaryText,
    modalTitle,
    modalSubtitle,
    supportMessage,
    // Handlers
    handleVerifyAccount,
    handleResend,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    resetOtp,
    setGstin,
    setGstUsername,
    setApiEnabled,
    setFormError,
    setFormServerError,
    // Tracking passthrough
    trackButton,
  };
}

// ── Credential deletion ─────────────────────────────────────────────────────

/**
 * Delete a GST credential from the backend.
 *
 * If `credentialId` is known (supplier-intelligence), it is used directly.
 * If not (lending/monthly-report), the credential is looked up by `gstin`
 * via `getCredentials` and then deleted.
 *
 * Returns `true` on success, `false` on failure.
 */
export async function deleteGstCredential(
  gstin: string,
  credentialId?: string
): Promise<boolean> {
  try {
    let id = credentialId;

    // If no credentialId, look it up by gstin
    if (!id) {
      const credsRes = await GstRestS.getCredentials();
      if (!credsRes?.success || !credsRes?.data) return false;
      const match = credsRes.data.find(
        (c: any) => c.gstin === gstin || c.gst_in === gstin
      );
      if (!match) return false;
      id = match.credentialId;
    }

    const res = await GstRestS.deleteCredential(id, gstin);
    return res?.success !== false;
  } catch {
    return false;
  }
}